import { ROAD_HALF_WIDTH } from '../constants.js';
import { ObstacleType } from '../types.js';
import type { StageDefinition } from './types.js';

function assert(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Error(message);
}

export function validateStageDefinition(stage: StageDefinition): StageDefinition {
  const prefix = `Stage "${stage.id}"`;
  assert(stage.order > 0, `${prefix}: order must be positive`);
  assert(stage.timeLimitSeconds > 0, `${prefix}: time limit must be positive`);
  assert(stage.beats.length > 0, `${prefix}: at least one beat is required`);
  assert(stage.target.contactT >= 0 && stage.target.contactT < 1, `${prefix}: invalid contactT`);
  assert(stage.target.escapeT > stage.target.contactT && stage.target.escapeT <= 1, `${prefix}: invalid escapeT`);
  assert(stage.target.maxHealth > 0, `${prefix}: target health must be positive`);

  const ids = new Set<string>();
  const useId = (id: string): void => {
    assert(id.length > 0, `${prefix}: empty authored id`);
    assert(!ids.has(id), `${prefix}: duplicate authored id "${id}"`);
    ids.add(id);
  };

  let beatEnd = 0;
  for (const beat of stage.beats) {
    useId(beat.id);
    assert(beat.startsAt === beatEnd, `${prefix}: beats must be contiguous at ${beat.id}`);
    assert(beat.endsAt > beat.startsAt && beat.endsAt <= 1, `${prefix}: invalid beat range ${beat.id}`);
    beatEnd = beat.endsAt;
  }
  assert(beatEnd === 1, `${prefix}: final beat must end at 1`);

  let previousSpawn = -1;
  let targetSpawnCount = 0;
  for (const event of stage.spawns) {
    useId(event.id);
    assert(event.t >= previousSpawn && event.t >= 0 && event.t < 1, `${prefix}: spawns must be sorted`);
    assert(event.count > 0, `${prefix}: spawn count must be positive`);
    if (event.isTarget) {
      targetSpawnCount++;
      assert(event.t === stage.target.contactT, `${prefix}: target spawn must match contactT`);
    }
    previousSpawn = event.t;
  }
  assert(targetSpawnCount === 1, `${prefix}: exactly one target spawn is required`);

  let previousObstacle = -1;
  for (const event of stage.obstacles) {
    useId(event.id);
    assert(event.t >= 0 && event.t < 1, `${prefix}: event ${event.id} is outside the rail`);
    assert(event.t >= previousObstacle, `${prefix}: obstacles must be sorted`);
    if (event.obstacleType === ObstacleType.RepairKit) {
      assert(event.laterals.every((lateral) => Math.abs(lateral) > ROAD_HALF_WIDTH), `${prefix}: repair kits must stay off road`);
    }
    previousObstacle = event.t;
  }

  let previousHazard = -1;
  for (const event of stage.hazards) {
    useId(event.id);
    assert(event.t >= 0 && event.t < 1, `${prefix}: event ${event.id} is outside the rail`);
    assert(event.t >= previousHazard, `${prefix}: hazards must be sorted`);
    previousHazard = event.t;
  }

  for (const component of stage.target.components) {
    useId(component.id);
    assert(component.maxHealth > 0 && component.radius > 0, `${prefix}: invalid component ${component.id}`);
    assert(component.exposedIn.length > 0, `${prefix}: component ${component.id} is never exposed`);
  }

  let previousThreshold = 2;
  for (const configuration of stage.target.configurations) {
    assert(configuration.healthThreshold <= previousThreshold, `${prefix}: target thresholds must descend`);
    assert(configuration.healthThreshold >= 0 && configuration.healthThreshold <= 1, `${prefix}: invalid target threshold`);
    previousThreshold = configuration.healthThreshold;
  }

  return stage;
}

export function validateStageCatalog(stages: readonly StageDefinition[]): readonly StageDefinition[] {
  assert(stages.length > 0, 'Stage catalog cannot be empty');
  const ids = new Set<string>();
  const orders = new Set<number>();
  for (const stage of stages) {
    validateStageDefinition(stage);
    assert(!ids.has(stage.id), `Duplicate stage id "${stage.id}"`);
    assert(!orders.has(stage.order), `Duplicate stage order ${stage.order}`);
    ids.add(stage.id);
    orders.add(stage.order);
  }
  return stages;
}
